import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Loader2, Send, Mail, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminNewsletterPage = () => {
  const { token } = useAuth();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [recipientType, setRecipientType] = useState('all');

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  const fetchNewsletters = async () => {
    try {
      const response = await axios.get(`${API}/admin/newsletters`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setNewsletters(response.data.newsletters || []);
    } catch (error) {
      console.error('Error fetching newsletters:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNewsletters();
  }, [token]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/newsletter`, {
        subject,
        content,
        recipient_type: recipientType
      }, {
        headers: getHeaders(),
        withCredentials: true
      });
      
      toast.success(`Newsletter sent to ${response.data.recipient_count} recipients`);
      setSubject('');
      setContent('');
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send newsletter');
    }
    setSending(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-newsletter">
      <h1 className="font-heading text-3xl font-bold mb-8">Newsletter</h1>

      <Alert className="mb-6 border-yellow-200 bg-yellow-50">
        <AlertCircle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-800">
          <strong>MOCKED:</strong> SendGrid API key required for actual email delivery. 
          Newsletter records are saved but emails won't be sent.
        </AlertDescription>
      </Alert>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Compose */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Compose Newsletter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSend} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Newsletter subject line"
                  required
                  data-testid="newsletter-subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="recipients">Recipients</Label>
                <Select value={recipientType} onValueChange={setRecipientType}>
                  <SelectTrigger data-testid="newsletter-recipients">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="subscribers">Subscribers Only</SelectItem>
                    <SelectItem value="recent_buyers">Recent Buyers</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Content *</Label>
                <Textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write your newsletter content here..."
                  rows={8}
                  required
                  data-testid="newsletter-content"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full btn-lift" 
                disabled={sending}
                data-testid="send-newsletter-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Newsletter
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* History */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Send History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {newsletters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                      No newsletters sent yet
                    </TableCell>
                  </TableRow>
                ) : (
                  newsletters.map((nl) => (
                    <TableRow key={nl.newsletter_id}>
                      <TableCell className="font-medium">{nl.subject}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{nl.recipient_count}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(nl.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
